import React, {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import queryString from "query-string";
import {IState} from "../../types/state";
import {useRouter} from "next/router";
import cls from "./posts.module.scss"
import Script from "next/script";
import {schemaPost} from "../schemaorg";
import {IPostData, IPostPageProps} from "../../types/posts";
import BlockLoader from "../blocks/BlockLoader";
import PostCard from "./PostCard";
import PageHeader from "../shared/PageHeader";
import Pagination from "../shared/Pagination";
import {CustomSelectArrow, PostCatsFilterIcon, PostTagsFilterIcon} from "../../svg";
import Link from "next/link";

const buildQuery = (options: { page: number }) => {
    const params = {page: 0};
    if (options.page !== 1) {
        params.page = options.page;
    }
    return queryString.stringify(params, {encode: false});
}

const PostPage = (props: IPostPageProps) => {
    const {
        posts: {
            current_page: page,
            ...posts
        },
        dbName,
        domain,
        postCategories,
        postTags,
        breadcrumb,
        translationsData,
        selectedLocale
    } = props;
    const router = useRouter();
    const [selectedCatSlug, setSelectedCatSlug] = useState<string>("all");
    const [selectedTags, setSelectedTags] = useState<Array<number>>([]);
    const [openDropDown, setOpenDropDown] = useState<boolean>(false);
    const [total, _] = useState<number>(posts.total);
    const schema = schemaPost(dbName, router.asPath, posts.data)
    const prevLocaleCodeRef: { current: string | undefined } = useRef();
    const prevPageRef: { current: number | undefined } = useRef();
    const catSelectRef = useRef<any>(null);

    useEffect(() => {
        prevPageRef.current = page;
        prevLocaleCodeRef.current = selectedLocale;

        const onClose = (e: MouseEvent) => {
            if (catSelectRef && catSelectRef.current && !catSelectRef.current.contains(e.target)) {
                setOpenDropDown(false)
            }
        }

        document.addEventListener("mousedown", onClose)

        return () => document.removeEventListener('mousedown', onClose)
    }, []);

    useEffect(() => {
        const {query} = router
        const {tags, category} = query

        if (tags) {
            setSelectedTags((tags as string).split(",").map(el => +el))
        } else setSelectedTags([])
        if (category) {
            setSelectedCatSlug(category as string)
        } else setSelectedCatSlug("all")

    }, [router.asPath]);

    const handlePageChange = async (page: number) => {
        if (!page || page === 1) {
            const updatedQuery = router?.query;
            delete updatedQuery.page;
            await router.push({ query: updatedQuery })
        } else {
            await router.push({ query: { ...router.query, page: page } })
        }
    };

    if (!posts.data) {
        return <BlockLoader/>;
    }

    const handleClickTags = (tag_id: number) => {
        let newState = [...selectedTags]
        if (selectedTags.includes(tag_id)) {
            newState = [...selectedTags.filter(el => el !== tag_id)]
        } else {
            newState.push(tag_id)
        }

        const queryStr = newState.join()
        router.push({
            query: queryString.stringify({
                ...router.query,
                tags: queryStr
            }, {encode: false})
        })
        setSelectedTags([...newState])
    }

    const handleClickCategories = (cat_slug: string) => {
        setSelectedCatSlug(cat_slug)
        setOpenDropDown(false)
    }

    const catSelectForMobile = () => {
        const selectedCategory = postCategories.find(el => el.slug === selectedCatSlug)
        const selectedOption = {
            true: {
                name: selectedCategory?.name,
                slug: selectedCategory?.slug,
                class: cls.selected
            },
            false: {
                name: translationsData.PostCategoriesAll,
                slug: "all",
                class: ""
            }
        }

        const cat_drop_down = {
            true: <div className={cls.cat_drop_down}>
                <div className={`${cls.cat_option} ${selectedOption[`${selectedCategory?.slug === undefined}`].class}`}
                     onClick={() => handleClickCategories("all")}>
                    {translationsData.PostCategoriesAll}
                </div>
                {
                    postCategories.map(cat => {
                        return <div
                            key={cat.id}
                            className={`${cls.cat_option} ${selectedOption[`${selectedCategory?.slug === cat.slug}`].class}`}
                            onClick={() => handleClickCategories(cat.slug)}
                        >
                            <Link href={`/posts/${cat.slug}`} style={{ display: "block" }}>
                                <a href={`/posts/${cat.slug}`}>
                                    {cat.name}
                                </a>
                            </Link>
                        </div>
                    })
                }
            </div>,
            false: null
        }

        const dropDownArrowClasses = {
            true: `${cls.drop_down_arrow} ${cls.opened}`,
            false: cls.drop_down_arrow
        }

        return <div className={`${cls.post_cat_filter_body} ${cls.mobile}`}>
            <div className={cls.cat_select} ref={catSelectRef}>
                <div className={cls.cat_button} onClick={() => setOpenDropDown(prevState => !prevState)}>
                    <span> {selectedOption[`${!!selectedCategory}`].name} </span>
                    <CustomSelectArrow className={dropDownArrowClasses[`${openDropDown}`]}/>
                </div>
                {cat_drop_down[`${openDropDown}`]}
            </div>
        </div>
    }

    const activeCategoryClasses = {
        true: `${cls.post_filters_row} ${cls.active}`,
        false: `${cls.post_filters_row}`
    }

    const postsList = posts.data.map((post: IPostData) => {
        return (
            <div key={post.id} className={cls.post_list_item}>
                <PostCard
                    post={post}
                    category={post.default_category}
                    translationsData={translationsData}
                    date_checked={true}
                    read_more_checked={true}
                />
            </div>
        );
    });

    const postFilters = <div className={cls.post_filters}>
        <div className={`${cls.post_category_filters} ${cls.post_filter}`}>
            <div className={cls.post_filter_title}>
                <PostCatsFilterIcon/>
                <span>
                    {translationsData.PostCategories}
                </span>
            </div>
            <div className={`${cls.post_cat_filter_body} ${cls.desktop}`}>
                <div className={activeCategoryClasses[`${selectedCatSlug === "all"}`]}
                     onClick={() => handleClickCategories("all")}>
                    <span>
                        {translationsData.PostCategoriesAll}
                    </span>
                </div>
                {
                    postCategories.map(cat => {
                        return <div
                            key={cat.id}
                            className={activeCategoryClasses[`${selectedCatSlug === cat.slug}`]}
                            onClick={() => handleClickCategories(cat.slug)}
                        >
                            <Link href={`/posts/${cat.slug}`}>
                                <a href={`/posts/${cat.slug}`}>
                                    {cat.name}
                                </a>
                            </Link>
                        </div>
                    })
                }
            </div>
            {catSelectForMobile()}
        </div>

        <div className={`${cls.post_tags_filters} ${cls.post_filter}`}>
            <div className={cls.post_filter_title}>
                <PostTagsFilterIcon/>
                <span>
                    {translationsData.Tags}
                </span>
            </div>
            <div className={cls.post_tag_filter_body}>
                {
                    postTags.map(tag => {
                        const tagClasses = {
                            true: `${cls.post_filters_tag} ${cls.active}`,
                            false: `${cls.post_filters_tag}`
                        }

                        return <div
                            key={tag.id}
                            className={tagClasses[`${selectedTags.includes(tag.id)}`]}
                            onClick={() => handleClickTags(tag.id)}
                        >
                            {tag.name}
                        </div>
                    })
                }
            </div>
        </div>
    </div>

    const filterIsHidden = {
        true: <div className={cls.post_body}>
            {postFilters}
            <div className={cls.posts_list}>
                {postsList}
            </div>
        </div>,
        false: <div className={cls.posts_list}>
            {postsList}
        </div>
    }

    const viewPosts = {
        true: <div className="block">
            {filterIsHidden[`${postCategories.length > 1}`]}
            <div className={cls.posts_pagination} style={{ margin: "47px 0" }}>
                {posts?.total <= 1 ? (
                // {postsList && postsList.length < 20 ? (
                    <></>
                ) : (
                    <Pagination
                        current={+page}
                        siblings={2}
                        total={total}
                        onPageChange={handlePageChange}
                    />
                )}
            </div>
        </div>,
        false: <div className={cls["products-view__empty"]}>
            <div className={cls["products-view__empty-title"]}>
                {translationsData.NothingFound}
            </div>
            <div className={cls["products-view__empty-subtitle"]}>
                {translationsData.TryResetFilters}
            </div>
            <button
                type="button"
                className="btn btn-orange btn-sm"
                onClick={() => {
                    router.push(router.pathname)
                }}
            >
                {translationsData.Reset}
            </button>
        </div>
    }

    return (
        <>
            <Script
                id={schema.name}
                type="application/ld+json"
                dangerouslySetInnerHTML={{__html: JSON.stringify(schema)}}
            />
            <PageHeader dbName={dbName} header="Latest News" breadcrumb={breadcrumb}/>
            <div className="container">
                <div className="row">
                    <h1 className={cls.post_page_title}>
                        {translationsData.Posts}
                    </h1>
                    <div>
                        {viewPosts[`${!!postsList.length}`]}
                    </div>
                </div>
            </div>
        </>
    );
};

export default PostPage;